import React from 'react';

import { loadRemote } from '@ps-int/fedya-versionator/dist/runtime';
import { createClientMFInit, getConfigFromWindow, loadMFComponent } from '@yandex-360/fedya-preloader/client';
import pick from 'lodash/pick';
import ReactDOM from 'react-dom';

import pkg from '../../../../../package.json';
import { createLogger } from '../../helpers/loggerFacade';
import { timeoutFactory } from '../../helpers/timeout-factory';
import {
    FALLBACK_COMPONENTS,
    FALLBACK_INIT_COMPONENTS,
    LOAD_COMPONENT_TIMEOUT,
    ModuleFederationComponents,
} from './constants';
import { initModuleFederation } from './init-mf';
import type { InitComponent, MFComponent, MFComponents, PDOKConfig, PreloadedData } from './types';

const logger = createLogger('module-federation');

type Component<T extends ModuleFederationComponents> = {
    default: MFComponent<T>;
};

class ModuleFederation {
    private pdokConfig: Maybe<PDOKConfig>;
    private components: Maybe<MFComponents>;
    private mfInitComponents: Maybe<InitComponent[]>;
    private FederationHost: Maybe<ReturnType<typeof initModuleFederation>>;

    constructor() {
        if (!NODE) {
            const { pdokConfig, sharedDependencies, mfInitComponents } = this.loadConfig();
            if (pdokConfig && sharedDependencies) {
                this.pdokConfig = pdokConfig;
                this.FederationHost = initModuleFederation(pdokConfig, sharedDependencies);
            }
            if (mfInitComponents) {
                this.mfInitComponents = mfInitComponents;
            }
        }
    }

    public getComponent<T extends ModuleFederationComponents>(
        key: ModuleFederationComponents
    ): MFComponent<T> | undefined {
        const fallback = FALLBACK_COMPONENTS[key] as MFComponent<T> | undefined;
        if (NODE && global && global?.components) {
            const component = global?.components?.[key];
            if (component) {
                return component as MFComponent<T>;
            }
            return fallback;
        }
        if (!NODE) {
            const component = this.components?.[key];
            if (component) {
                return component as MFComponent<T>;
            }
            return fallback;
        }
        return fallback;
    }

    public async initMfComponents() {
        if (!NODE && this.pdokConfig) {
            try {
                const mfInitComponentsNames = this.mfInitComponents?.map((component: InitComponent) => component.name);
                const componentsToLoad = pick<PDOKConfig>(
                    this.pdokConfig,
                    mfInitComponentsNames ?? FALLBACK_INIT_COMPONENTS
                );
                const componentPromises = Object.entries(componentsToLoad).map(([component, config]) => {
                    if (config && this.checkIsKnownComponent(component)) {
                        return this.loadComponent<typeof component>(component);
                    }
                    return Promise.reject('Unknown component');
                });
                const results = await Promise.allSettled(componentPromises);
                results.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        const [componentName, component] = result.value;
                        if (componentName && this.checkIsKnownComponent(componentName) && component) {
                            this.setComponent(componentName, component);
                        }
                    }
                });
            } catch (error) {
                logger.error(error, {
                    method: 'initMfComponents',
                });
            }
        }
    }

    private checkIsKnownComponent(componentName: unknown): componentName is ModuleFederationComponents {
        if (String(componentName) in ModuleFederationComponents) {
            return true;
        }
        return false;
    }

    private setComponent(name: ModuleFederationComponents, component: unknown) {
        if (!this.components) {
            this.components = {};
        }
        if (name && component) {
            this.components[name] = component;
        }
    }

    private async loadComponent<T extends ModuleFederationComponents>(componentKey: ModuleFederationComponents) {
        try {
            const component = await Promise.race([
                loadRemote<Component<T>>(`${String(componentKey)}/${componentKey}`),
                timeoutFactory(LOAD_COMPONENT_TIMEOUT, { method: 'loadComponent', additional: { componentKey } }),
            ]);
            if (component) {
                return [componentKey, component] as [ModuleFederationComponents, Component<T>];
            } else {
                return Promise.reject();
            }
        } catch (error) {
            logger.error(error, {
                method: 'loadComponent',
            });
            return Promise.reject();
        }
    }

    private loadConfig() {
        try {
            const pdokConfig: PDOKConfig = JSON.parse(
                document.getElementById('pdok-config')?.innerHTML || '{}'
            )?.pdokConfig;
            const preloadedData: PreloadedData = JSON.parse(
                document.getElementById('preloaded-data')?.innerHTML || '{}'
            );
            const { sharedDependencies, mfInitComponents } = preloadedData;

            return { pdokConfig, sharedDependencies, mfInitComponents };
        } catch (error) {
            logger.error(error, { method: 'loadConfig' });
            return { pdokConfig: null, sharedDependencies: null };
        }
    }
}

class FedyaModuleFederation {
    private clientMF: Maybe<ReturnType<typeof createClientMFInit>>;
    private components: MFComponents = {};

    constructor() {
        if (!NODE) {
            this.clientMF = createClientMFInit({
                serviceName: 'telemost',
                sharedModules: {
                    react: () => React,
                    'react-dom': () => ReactDOM,
                },
            });
        }
    }

    public getComponent<T extends ModuleFederationComponents>(
        key: ModuleFederationComponents
    ): MFComponent<T> | undefined {
        const fallback = FALLBACK_COMPONENTS[key] as MFComponent<T> | undefined;
        if (NODE) {
            const component = global?.fedyaComponents?.[key];
            return (component as MFComponent<T>) || fallback;
        } else {
            const component = this.components?.[key];
            return (component as MFComponent<T>) || fallback;
        }
    }

    public async initMfComponents() {
        const fedyaConfig = getConfigFromWindow();
        if (fedyaConfig?.config && this.clientMF) {
            this.clientMF.initModuleFederation(fedyaConfig?.config, pick(pkg.dependencies, ['react', 'react-dom']));
            await Promise.all(
                Object.keys(fedyaConfig.config).map(async (componentName) => {
                    const module = await loadMFComponent(componentName, fedyaConfig.config);
                    if (module) {
                        this.setComponent(componentName as ModuleFederationComponents, module);
                    }
                })
            );
        }
    }

    private setComponent(name: ModuleFederationComponents, component: unknown) {
        if (name && component) {
            this.components[name] = component;
        }
    }
}

export const moduleFederation = new ModuleFederation();
export const fedyaModuleFederation = new FedyaModuleFederation();
